import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const DATE_GRAPH_LABEL_WIDTH = 14;
const DATE_GRAPH_CELL_SIZE = 12;
const DATE_GRAPH_GAP = 3;

const DateGraphSection = styled.section`
  margin: 0;
  padding: 0 8px;
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  /* 確保 scrollIntoView 不會把 active cell 推到 sticky 星期 label 底下 */
  scroll-padding-inline-start: ${DATE_GRAPH_LABEL_WIDTH + DATE_GRAPH_GAP}px;
  @media (max-width: 480px) {
    scroll-padding-inline-start: ${10 + 2}px;
  }
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DateGraphTable = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  min-width: min-content;
  margin: 0;
`;

const DateGraphMonthRow = styled.div`
  display: flex;
  gap: ${DATE_GRAPH_GAP}px;
  align-items: flex-end;
  height: 14px;
  @media (max-width: 480px) {
    height: 12px;
    gap: 2px;
  }
`;

const DateGraphMonthLabelSpacer = styled.span`
  width: ${DATE_GRAPH_LABEL_WIDTH}px;
  flex-shrink: 0;
  position: sticky;
  left: 0;
  z-index: 1;
  @media (max-width: 480px) {
    width: 10px;
  }
`;

const DateGraphMonthCells = styled.div`
  display: flex;
  gap: ${DATE_GRAPH_GAP}px;
  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const DateGraphMonthCell = styled.span`
  width: ${DATE_GRAPH_CELL_SIZE + 2}px;
  box-sizing: border-box;
  border: 1px solid transparent;
  font-size: 0.65rem;
  opacity: 0.75;
  flex-shrink: 0;
  text-align: left;
  letter-spacing: -0.03em;
  overflow: visible;
  @media (max-width: 480px) {
    width: 10px;
    font-size: 0.55rem;
  }
`;

const DateGraphBodyRow = styled.div`
  display: flex;
  gap: ${DATE_GRAPH_GAP}px;
  align-items: center;
  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const DateGraphDayLabel = styled.span`
  width: ${DATE_GRAPH_LABEL_WIDTH}px;
  font-size: 0.7rem;
  opacity: 0.8;
  flex-shrink: 0;
  position: sticky;
  left: 0;
  z-index: 1;
  pointer-events: none;
  background: transparent;
  text-align: right;
  @media (max-width: 480px) {
    width: 10px;
    font-size: 0.6rem;
  }
`;

const DateGraphCells = styled.div`
  display: flex;
  gap: ${DATE_GRAPH_GAP}px;
  @media (max-width: 480px) {
    gap: 2px;
  }
`;

const DateGraphCell = styled.span`
  width: ${DATE_GRAPH_CELL_SIZE + 2}px;
  height: ${DATE_GRAPH_CELL_SIZE + 2}px;
  box-sizing: border-box;
  border-radius: 2px;
  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
    border-radius: 1px;
  }
  border: ${({ $hasPhotos, $isVinyl }) => {
    if ($isVinyl) return '1px solid rgba(255, 230, 160, 0.95)';
    if ($hasPhotos) return '1px solid rgba(255, 255, 255, 0.7)';
    return '1px solid transparent';
  }};
  background: ${({ $level, $isActive, $hasPhotos, $isVinyl }) => {
    if ($isActive) {
      // 目前正在看的日期，維持亮黃色
      return '#f2c94c';
    }
    if ($isVinyl) {
      // 唱片特別日期：偏紫金色系
      return 'linear-gradient(135deg, #bb6bd9, #f2c94c)';
    }
    if ($level === 0) return 'rgba(255, 255, 255, 0.06)';
    if ($level === 1) return $hasPhotos ? 'rgba(56, 173, 140, 0.9)' : 'rgba(45, 155, 120, 0.5)';
    if ($level === 2) return $hasPhotos ? 'rgba(56, 173, 140, 1)' : 'rgba(45, 155, 120, 0.75)';
    if ($level === 3) return $hasPhotos ? 'rgba(33, 140, 110, 1)' : 'rgba(45, 155, 120, 0.95)';
    return $hasPhotos ? '#218c6e' : '#2d9b78';
  }};
  transition: transform 0.1s ease, box-shadow 0.1s ease;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`;

const DateGraphTooltip = styled.div`
  position: fixed;
  z-index: 100;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(15, 18, 25, 0.96);
  color: #e9ecf1;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
  pointer-events: none;
  max-width: 280px;
  line-height: 1.45;
`;

const DAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

export function DateGraph({
  dateGraphGrid,
  monthLabels,
  activeDateKey,
  tooltip,
  setTooltip,
  onCellClick,
  displayRowOrder = [0, 1, 2, 3, 4, 5, 6],
}) {
  const sectionRef = useRef(null);

  // 配合卡片捲動：當 activeDateKey 改變時，讓 date graph 橫向捲動到該日期格
  useEffect(() => {
    if (!activeDateKey || !sectionRef.current) return;
    const el = sectionRef.current.querySelector(`[data-date="${activeDateKey}"]`);
    if (el) {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [activeDateKey]);

  return (
    <>
      <DateGraphSection ref={sectionRef}>
        <DateGraphTable>
          <DateGraphMonthRow>
            <DateGraphMonthLabelSpacer aria-hidden />
            <DateGraphMonthCells>
              {monthLabels.map((label, col) => (
                <DateGraphMonthCell key={col}>{label}</DateGraphMonthCell>
              ))}
            </DateGraphMonthCells>
          </DateGraphMonthRow>
          {displayRowOrder.map((dayOfWeek, displayRowIdx) => (
            <DateGraphBodyRow key={dayOfWeek}>
              <DateGraphDayLabel>{DAY_LABELS[displayRowIdx]}</DateGraphDayLabel>
              <DateGraphCells>
                {dateGraphGrid[dayOfWeek].map((cell, col) => (
                  <DateGraphCell
                    key={`${dayOfWeek}-${col}`}
                    data-date={cell.date}
                    $level={cell.level}
                    $isActive={cell.date === activeDateKey}
                    $hasPhotos={cell.hasPhotos}
                    $isVinyl={cell.isVinyl}
                    onMouseEnter={(e) => {
                      const rect = e.target.getBoundingClientRect();
                      setTooltip({
                        date: cell.date,
                        summary: cell.summary,
                        count: cell.count,
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                      });
                    }}
                    onMouseMove={(e) => {
                      setTooltip((prev) =>
                        prev ? { ...prev, x: e.clientX, y: e.clientY } : null
                      );
                    }}
                    onMouseLeave={() => setTooltip(null)}
                    onClick={() => {
                      if (cell.firstIdx != null) {
                        onCellClick(cell.firstIdx);
                      }
                    }}
                    title={cell.date}
                  />
                ))}
              </DateGraphCells>
            </DateGraphBodyRow>
          ))}
        </DateGraphTable>
      </DateGraphSection>
      {tooltip && (
        <DateGraphTooltip
          style={{
            left: Math.min(tooltip.x + 12, window.innerWidth - 300),
            top: tooltip.y - 8,
            transform: 'translateY(-100%)',
          }}
        >
          {tooltip.date}
        </DateGraphTooltip>
      )}
    </>
  );
}
